import { toEither, Async, Loaded } from 'Core/models/Async';
import { filter, flatten, head } from 'fp-ts/lib/Array';
import * as E from 'fp-ts/lib/Either';
import { constant, identity } from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import { pipe } from 'fp-ts/lib/pipeable';
import { ReducerState } from 'rootReducer';
import { AgentNumberStatusId } from 'Shared/constants/AgentNumberStatus';
import { TerminatedAgentScopes } from 'Shared/constants/Scope';
import { eq } from 'Shared/utilities/equals';
import { UserProfileState } from 'Users/ducks/userProfile';
import { AgentClaims } from 'Users/models/AgentClaims';
import { AsyncUserClaims, UserClaims, UserTypeClaimKey, UserTypeClaims } from 'Users/models/Claims';

export const isAgencyInStatus = (state: ReducerState, status: AgentNumberStatusId) => {
  return pipe(
    state.user.profile.selectedAgency,
    O.fold(
      () => false,
      selectedAgency => selectedAgency.overallAgentNumberStatus.id === status
    )
  );
};

export const getUserClaims = (userClaims: UserClaims) => {
  const getAgentClaims = (agentClaims: AgentClaims) =>
    pipe(
      O.fromNullable(agentClaims.impersonatedAgentClaim),
      O.fold(
        () => userClaims,
        (claims): UserClaims => ({
          ...claims,
          isImpersonating: true,
          originalUserName: userClaims.userName,
          userTypes: [
            {
              $type: UserTypeClaimKey.AgentSite,
              ...claims,
            },
          ],
        })
      )
    );

  const result = pipe(
    userClaims.userTypes,
    filter(userTypes => userTypes.$type === UserTypeClaimKey.AgentSite),
    head,
    O.fold(
      () => userClaims,
      agentClaims => (agentClaims.$type === UserTypeClaimKey.AgentSite ? getAgentClaims(agentClaims) : userClaims)
    )
  );

  return result;
};

export const selectCurrentTaxIdUserProfile = (state: UserProfileState) =>
  pipe(
    state.selectedAgency,
    O.fold(constant(''), agency => agency.maskedIdentifier)
  );

const selectAgentScopesUserProfile = (state: UserProfileState, agentClaims: AgentClaims) => {
  const currentAgency = O.fromNullable(
    agentClaims.agencyAssociations.find(eq('maskedIdentifier', selectCurrentTaxIdUserProfile(state)))
  );

  if (O.isSome(currentAgency) && currentAgency.value.overallAgentNumberStatus.id === AgentNumberStatusId.Terminated) {
    return TerminatedAgentScopes;
  }

  return pipe(
    O.fromNullable(agentClaims.agencyAssociations.find(eq('maskedIdentifier', selectCurrentTaxIdUserProfile(state)))),
    O.fold(constant([]), agency => agency.scopes)
  );
};

export const getUserScopesByClaimKey = (state: UserProfileState, userClaims: UserClaims) => (
  userTypeClaimKey: UserTypeClaimKey
) => {
  const unflattenedUserProfileInformation = getUserScopesUserProfileUnflattened(state, userClaims)!.find(
    x => x!['$type'] === userTypeClaimKey
  );

  return unflattenedUserProfileInformation ? unflattenedUserProfileInformation!['scopes'] : undefined;
};

export const loadUserClaims = (asyncClaims: Async<UserClaims>) => {
  const data = toEither(asyncClaims);
  const claims: UserClaims | undefined = pipe(
    data,
    E.map(value => new Loaded(value)),
    E.fold(
      () => undefined,
      value => value.Data
    )
  )!;

  return claims;
};

export const hasUserTypeClaimKey = (userClaims: UserClaims, userTypeClaimKey: UserTypeClaimKey) => {
  return userClaims.userTypes.some(userType => userType.$type === userTypeClaimKey);
};

export const getUserScopesUserProfileUnflattened = (state: UserProfileState, userClaims: UserClaims) => {
  return (userClaims.userTypes || []).map(userType =>
    UserTypeClaims.match(userType, {
      [UserTypeClaimKey.MyAccessPortal]: portalClaims => {
        return {scopes: portalClaims.scopes, $type: userType.$type};
      },
      [UserTypeClaimKey.AgentSite]: agent => {
        const impersonatedAgentClaims = O.fromNullable(agent.impersonatedAgentClaim);

        return O.isSome(impersonatedAgentClaims)
          ? {
              scopes: selectAgentScopesUserProfile(state, impersonatedAgentClaims.value),
              $type: userType.$type,
            }
          : agent.agencyAssociations.length > 0
          ? {
              scopes: selectAgentScopesUserProfile(state, agent),
              $type: userType.$type,
            }
          : {
              scopes: agent.scopes,
              $type: userType.$type,
            };
      },
      Empty: undefined,
      default: () => {
        return undefined;
      },
    })
  );
};

export const getUserScopesUserProfile = (state: UserProfileState, userClaims: UserClaims) => {
  return flatten(
    (userClaims.userTypes || []).map(userType =>
      UserTypeClaims.match(userType, {
        [UserTypeClaimKey.MyAccessPortal]: portalClaims => portalClaims.scopes,
        [UserTypeClaimKey.AgentSite]: agent => {
          const impersonatedAgentClaims = O.fromNullable(agent.impersonatedAgentClaim);
          return O.isNone(impersonatedAgentClaims)
            ? agent.agencyAssociations.length > 0
              ? selectAgentScopesUserProfile(state, agent)
              : agent.scopes
            : O.isSome(impersonatedAgentClaims)
            ? selectAgentScopesUserProfile(state, impersonatedAgentClaims.value)
            : [];
        },
        Empty: constant([]),
        default: userType => {
          return [];
        },
      })
    )
  );
};

const selectAgentScopes = (state: ReducerState, agentClaims: AgentClaims) => {
  const currentAgency = O.fromNullable(
    agentClaims.agencyAssociations.find(eq('maskedIdentifier', selectCurrentTaxId(state)))
  );
  if (O.isSome(currentAgency) && currentAgency.value.overallAgentNumberStatus.id === AgentNumberStatusId.Terminated) {
    return TerminatedAgentScopes;
  }

  return pipe(
    O.fromNullable(agentClaims.agencyAssociations.find(eq('maskedIdentifier', selectCurrentTaxId(state)))),
    O.fold(constant([]), agency => agency.scopes)
  );
};

export const getUserScopes = (state: ReducerState, userClaims: UserClaims) => {
  return flatten(
    (userClaims.userTypes || []).map(userType =>
      UserTypeClaims.match(userType, {
        [UserTypeClaimKey.MyAccessPortal]: portalClaims => portalClaims.scopes,
        [UserTypeClaimKey.AgentSite]: agent => {
          const impersonatedAgentClaims = O.fromNullable(agent.impersonatedAgentClaim);

          return O.isSome(impersonatedAgentClaims)
            ? selectAgentScopes(state, impersonatedAgentClaims.value)
            : agent.agencyAssociations.length > 0
            ? selectAgentScopes(state, agent)
            : agent.scopes;
        },
        Empty: constant([]),
        default: userType => {
          return [];
        },
      })
    )
  );
};

export const selectUserScopesEither = (state: ReducerState) => {
  return pipe(
    E.right((claims: UserClaims) => getUserScopes(state, claims)),
    E.ap(toEither(state.user.profile.userClaims))
  );
};

export const selectUserScopes = (state: ReducerState) => {
  return pipe(
    E.right((claims: UserClaims) => getUserScopes(state, claims)),
    E.ap(toEither(state.user.profile.userClaims)),
    E.fold(constant([]), identity)
  );
};
export const selectUserClaims = (state: ReducerState): AsyncUserClaims => {
  const getCurrentUserClaims = constant(state.user.profile.userClaims);
  return getCurrentUserClaims();
};
export const selectCurrentAgentNumbers = (state: ReducerState) => state.user.profile.currentAgentNumbers;
export const selectCurrentTaxId = (state: ReducerState) =>
  pipe(
    state.user.profile.selectedAgency,
    O.fold(constant(''), agency => agency.maskedIdentifier)
  );
export const selectCurrentIdentifier = (state: ReducerState) =>
  pipe(
    state.user.profile.selectedAgency,
    O.fold(constant(''), agency => agency.encryptedIdentifier)
  );
